import React, { useEffect, useState } from 'react';

// MUI Components
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

// MUI Icons
import {
  Add as AddIcon,
  ArrowDropDownOutlined as ArrowDropDownOutlinedIcon,
  Calculate as CalculateIcon,
  ChangeCircleOutlined as ChangeCircleOutlinedIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  TurnSharpRight as TurnSharpRightIcon,
} from "@mui/icons-material";

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

// Utilities & Helpers
import {
  calcRefFees,
  convertsMass,
  fetchWrapper,
  fetchWrapperNoAuth,
  float2Decimal,
  getCostPerItemId,
  SERVER,
  setCostPerItemId
} from '../../utils/helpers';
import { CATEGORIES_SF, PRODUCT_TYPES } from './helpers';

// Components
import DraggablePopup from '../DraggableIframe';
import ProductTypeSelector from '../Modals/ProductTypeSelector';
import SchemaForm from './SchemaForm';
import EnhancedSpinningLogo from '../Loaders/SpinningLogo';

// Modals
import ErrorModal from '../Modals/ErrorModal';
import SKUBuilderModal from '../Modals/SKUBuilderModal';

// Tooltips & Loaders
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import CustomCircularProgress from '../Loaders/CustomCircularProgress';

// Inputs
import CustomInput from '../../components/Inputs/CustomInput';
import SelectComponent from '../../components/Inputs/SelectComponent';
import CostPriceInput from '../Inputs/CostPriceInput';

const WarningInput = ({ type, isWarning, ...props }) => {
  const { theme } = useTheme();

  return (
    <TextField
      sx={{
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            // borderColor: customtheme.accent.warning, // Updated border color
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&:hover fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&.Mui-focused fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
        },
        "& .MuiInputLabel-root": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
          fontFamily: theme.fontFamily,
          "&.Mui-focused": {
            color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
          },
        },
        "& .MuiOutlinedInput-input": {
          fontFamily: theme.fontFamily,
          color: theme.text.primary, // Updated input text color
        },
      }}
      {...props}
      label={<Box sx={{ mr: "8px" }}>{isWarning ? `${props.label} - optional` : props.label}</Box>}
      type={type}
      slotProps={{
        input: props.InputProps,
      }}
      customtheme={theme}
    />
  );
};

const SourceInput = ({ sources = [], isWarning, value, onChange }) => {
  const { theme } = useTheme();
  const handleChange = (event, newValue) => {
    // Call the parent's onChange with the new value
    if (onChange) {
      onChange(newValue);
    }
  };

  const styles = {
    formControl: {
      mb: 2,
      borderRadius: 1.5,
      '& .MuiOutlinedInput-root': {
        '&:hover fieldset': {
          borderColor: isWarning ? theme.accent.warning : theme.text.primary,
        },
        '&.Mui-focused fieldset': {
          borderColor: isWarning ? theme.accent.warning : theme.text.primary,
        }
      },
      "& .MuiInputLabel-root": {
        color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
        },
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          // borderColor: customtheme.accent.warning, // Updated border color
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
        "&:hover fieldset": {
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
        "&.Mui-focused fieldset": {
          border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
        },
      },
      "& .MuiInputLabel-root": {
        color: isWarning ? theme.accent.warning : theme.text.primary, // Updated label color
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: isWarning ? theme.accent.warning : theme.text.primary, // Updated focused label color
        },
      },
      "& .MuiOutlinedInput-input": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary, // Updated input text color
      },
      ".MuiAutocomplete-clearIndicator": {
        color: isWarning ? theme.accent.warning : theme.text.primary
      },
    }
  }

  return (
    <FormControl fullWidth sx={styles.formControl}>
      <Autocomplete
        freeSolo
        options={sources.filter(item => !!item) || []}
        value={value && value !== 'null' && value !== 'undefined' ? value : ''}
        onChange={handleChange}
        inputValue={value && value !== 'null' && value !== 'undefined' ? value : ''}
        onInputChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={styles.textField}
            label={<Box sx={{ mr: "8px" }}>{isWarning ? 'Source - optional' : 'Source'}</Box>}
            variant="outlined"
            size="small"
          />
        )}
        renderOption={(props, option) => (
          <Box key={option} component="li" {...props}>
            <Box>
              <Typography variant="body2" sx={{ fontFamily: theme.fontFamily }}>
                {option}
              </Typography>
            </Box>
          </Box>
        )}
      />
    </FormControl>
  );
};

const WarningEvaluateInput = ({ type, isWarning, onExpressionEvaluated, ...props }) => {
  const { theme } = useTheme();
  const [displayValue, setDisplayValue] = useState(props.value || "");

  // Safe math expression evaluator
  const evaluateExpression = (expression) => {
    try {
      // Only allow numbers, operators, parentheses, and decimal points
      const safeExpression = expression.replace(/[^0-9+\-*/.() ]/g, '');

      // Prevent empty or invalid expressions
      if (!safeExpression || safeExpression.trim() === '') {
        return expression;
      }

      // Use Function constructor instead of eval for better security
      const result = new Function('return ' + safeExpression)();

      // Check if result is a valid number
      if (typeof result === 'number' && !isNaN(result)) {
        return result.toFixed(3);
      }

      return expression; // Return original if evaluation fails
    } catch (error) {
      return expression; // Return original expression if there's an error
    }
  };

  const handleBlur = (e) => {
    const evaluatedValue = evaluateExpression(displayValue);
    setDisplayValue(evaluatedValue);

    // Call the original onChange with evaluated value
    if (props.onChange) {
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: evaluatedValue }
      };
      props.onChange(syntheticEvent);
    }

    // Call onExpressionEvaluated if provided
    if (onExpressionEvaluated) {
      onExpressionEvaluated(evaluatedValue);
    }

    // Call original onBlur if provided
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // This will trigger handleBlur
    }

    // Call original onKeyPress if provided
    if (props.onKeyPress) {
      props.onKeyPress(e);
    }
  };

  const handleChange = (e) => {
    setDisplayValue(e.target.value);

    // Don't call the original onChange here - we'll do it on blur
    // This prevents the parent state from updating with partial expressions
  };

  // Update local state when props.value changes
  useEffect(() => {
    setDisplayValue(props.value || "");
  }, [props.value]);

  return (
    <TextField
      {...props}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyPress={handleKeyPress}
      sx={{
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&:hover fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
          "&.Mui-focused fieldset": {
            border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
          },
        },
        "& .MuiInputLabel-root": {
          color: isWarning ? theme.accent.warning : theme.text.primary,
          fontFamily: theme.fontFamily,
          "&.Mui-focused": {
            color: isWarning ? theme.accent.warning : theme.text.primary,
          },
        },
        "& .MuiOutlinedInput-input": {
          fontFamily: theme.fontFamily,
          color: theme.text.primary,
        },
      }}
      label={<Box sx={{ mr: "8px" }}>{isWarning ? `${props.label} - optional` : props.label}</Box>}
      type={type}
      slotProps={{
        input: props.InputProps,
      }}
    />
  );
};

const SKU_BUILDER_LIST = [
  { selected: true, name: "Source" },
  { selected: true, name: "Cost Price" },
  { selected: true, name: "Selling Price" },
  { selected: false, name: "Barcode" },
  { selected: false, name: "Item ID" },
  { selected: true, name: "Brand" },
  { selected: true, name: "Quantity" },
  { selected: true, name: "Profit" },
  { selected: true, name: "Breakeven" },
  { selected: false, name: "Expiration Date" },
  { selected: false, name: "Purchase Date" },
  { selected: false, name: "Note" },
  { selected: false, name: "Random No." }
];

function FormSF(props) {
  const { theme } = useTheme();
  const { encodedAuth, user, password, isSubUser } = useAuth();

  const [openSkuBuilderModal, setOpenSkuBuilderModal] = useState(false);
  const [openErrorModal, setopenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [inProgress, setInProgress] = useState(false);

  // Dimensions
  const [weight, setWeight] = useState(0);
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [anchorElWeight, setAnchorElWeight] = useState("");
  const openWeight = Boolean(anchorElWeight);

  const [length, setLength] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const [schema, setSchema] = useState(Object.keys(props.currProduct?.schema || {}).includes('$schema') ? props.currProduct?.schema : null);
  const [formData, setFormData] = useState(props.currProduct?.MPItem?.Visible || props.currProduct?.mp_item?.Visible || {});

  const [loading, setLoading] = useState(false);
  const [skuUpdate, setSkuUpdate] = useState(false);
  const [itemId, setItemId] = useState("");
  const [productType, setProductType] = useState("");
  const [productTypeInput, setProductTypeInput] = useState("");
  const [additionalCosts, setAdditionalCosts] = useState("");

  // Orderable
  const [fulfillmentCenterID, setFulfillmentCenterID] = useState(localStorage.getItem('IFFCID') || "");
  const [chemicalAerosolPesticide, setChemicalAerosolPesticide] = useState("No");
  const [electronicsIndicator, setElectronicsIndicator] = useState("No");
  const [batteryTechnologyType, setBatteryTechnologyType] = useState("Does Not Contain a Battery");

  // States for Bulk Items
  // SupplierItemFeedHeader
  const [categoryName, setCategoryName] = useState("");

  const [source, setSource] = useState(props.currProduct.source || localStorage.getItem('IFSource') || "");
  const [sources, setSources] = useState(JSON.parse(localStorage.getItem('IFSources') || "[]") || []);
  const [cost, setCost] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationDateInputType, setExpirationDateInputType] = useState('text');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toLocaleDateString());
  const [purchaseDateInputType, setPurchaseDateInputType] = useState('text');
  const [note, setNote] = useState("");

  // SupplierItem
  // Orderable
  const [sku, setSku] = useState("");
  const [skuFound, setSkuFound] = useState(false);
  const [brand, setBrand] = useState("");
  const [barcode, setBarcode] = useState("");
  const [changeBarcode, setChangeBarcode] = useState(props.barcode !== "");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);

  const [stateRestrictions, setStateRestrictions] = useState([]);
  const [stateRestrictionsText, setStateRestrictionsText] = useState("None");
  const [stateRestrictionsStates, setStateRestrictionsStates] = useState("");
  const [stateRestrictionsZipCodes, setStateRestrictionsZipCodes] = useState("");

  const [skuInputs, setSkuInputs] = useState(() => {
    try {
      const storedOrder = localStorage.getItem('IFSKUInputs');
      return storedOrder ? JSON.parse(storedOrder) : SKU_BUILDER_LIST;
    } catch (error) {
      return SKU_BUILDER_LIST;
    }
  });
  const [skuSeperator, setSkuSeperator] = useState(() => {
    try {
      const storedSeperator = localStorage.getItem('IFSKUSeperator');
      return storedSeperator ? storedSeperator : "_";
    } catch (error) {
      return "_";
    }
  });

  const resetSkuInputs = () => {
    setSkuInputs(SKU_BUILDER_LIST);
    localStorage.removeItem("IFSKUInputs");
  };

  const validateSchemaForm = (schema, productType, formData) => {
    // Result object
    const result = {
      isValid: true,
      errors: []
    };

    // Check if schema, productType, and formData exist
    if (!schema || !schema.properties || !schema.properties[productType] || !formData || !formData[productType]) {
      result.isValid = false;
      result.errors.push('Invalid schema or form data');
      return result;
    }

    const productSchema = schema.properties[productType];
    const productData = formData[productType];

    // 1. Check static required fields
    const requiredFields = productSchema.required || [];

    for (const fieldName of requiredFields) {
      if (!validateField(productSchema.properties[fieldName], productData[fieldName])) {
        result.isValid = false;
        result.errors.push(`Required field "${fieldName}" is missing or invalid`);
      }
    }

    // 2. Check conditional required fields (from allOf conditions)
    const allOfConditions = productSchema.allOf || [];

    allOfConditions.forEach(condition => {
      // Check if the condition "if" clause matches current form data
      if (condition.if && condition.if.properties && condition.then && condition.then.required) {
        let conditionMet = true;

        // Check each property in the "if" condition
        Object.entries(condition.if.properties).forEach(([fieldName, expectedValue]) => {
          const currentValue = productData[fieldName];

          // If any condition doesn't match, mark as not met
          if (expectedValue.enum) {
            // Handle enum conditions (match any value in enum)
            if (Array.isArray(currentValue)) {
              for (let i = 0; i < currentValue.length; i++) {
                if (!expectedValue.enum.includes(currentValue[i])) {
                  conditionMet = false;
                  break;
                }
              }
            } else {
              if (!expectedValue.enum.includes(currentValue)) {
                conditionMet = false;
              }
            }

          } else if (expectedValue.const !== undefined && currentValue !== expectedValue.const) {
            // Handle const conditions (exact match)
            conditionMet = false;
          } else if (expectedValue?.contains?.enum) {
            if (Array.isArray(currentValue)) {
              for (let i = 0; i < currentValue.length; i++) {
                if (!expectedValue?.contains?.enum.includes(currentValue[i])) {
                  conditionMet = false;
                  break;
                }
              }
            } else {
              if (!expectedValue?.contains?.enum.includes(currentValue)) {
                conditionMet = false;
              }
            }
          }
        });

        // If all conditions are met, validate the "then.required" fields
        if (conditionMet) {
          condition.then.required.forEach(fieldName => {
            if (!validateField(productSchema.properties[fieldName], productData[fieldName])) {
              result.isValid = false;
              result.errors.push(`Conditionally required field "${fieldName}" is missing or invalid`);
            }
          });
        }
      }
    });

    return result;
  };

  const validateField = (fieldSchema, fieldValue) => {
    if (!fieldSchema) return false;

    // Handle different field types
    switch (fieldSchema.type) {
      case 'string':
        // Check if the string exists and meets length requirements
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
          return false;
        }
        if (fieldSchema.minLength && fieldValue.length < fieldSchema.minLength) {
          return false;
        }
        if (fieldSchema.maxLength && fieldValue.length > fieldSchema.maxLength) {
          return false;
        }
        // If this is an enum, check if the value is in the allowed list
        if (fieldSchema.enum && !fieldSchema.enum.includes(fieldValue)) {
          return false;
        }
        return true;

      case 'number':
      case 'integer':
        // Check if the number exists and meets range requirements
        if (fieldValue === undefined || fieldValue === null || isNaN(fieldValue)) {
          return false;
        }
        if (fieldSchema.minimum !== undefined && fieldValue < fieldSchema.minimum) {
          return false;
        }
        if (fieldSchema.maximum !== undefined && fieldValue > fieldSchema.maximum) {
          return false;
        }
        return true;

      case 'boolean':
        // For boolean, just check if it's defined (can be true or false)
        return fieldValue !== undefined && fieldValue !== null;

      case 'array':
        // Check if array exists and meets length requirements
        if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
          return false;
        }
        if (fieldSchema.minItems && fieldValue.length < fieldSchema.minItems) {
          return false;
        }
        if (fieldSchema.maxItems && fieldValue.length > fieldSchema.maxItems) {
          return false;
        }
        // Validate each item in the array if needed
        if (fieldSchema.items) {
          for (const item of fieldValue) {
            if (!validateField(fieldSchema.items, item)) {
              return false;
            }
          }
        }
        return true;

      case 'object':
        // Check if object exists
        if (!fieldValue || typeof fieldValue !== 'object') {
          return false;
        }
        // Validate required properties if any
        if (fieldSchema.required) {
          for (const requiredProp of fieldSchema.required) {
            if (!validateField(
              fieldSchema.properties[requiredProp],
              fieldValue[requiredProp]
            )) {
              return false;
            }
          }
        }
        // For complex objects like measures with units
        if (fieldSchema.properties) {
          if (fieldSchema.properties.measure && fieldValue.measure === undefined) {
            return false;
          }
          if (fieldSchema.properties.unit && fieldValue.unit === undefined) {
            return false;
          }
        }
        return true;

      default:
        return false;
    }
  };

  const handleWeightUnitChange = (unit) => {
    const newWeight = convertsMass(parseFloat(weight), weightUnit, unit);
    setWeight(newWeight.toFixed(2));
    setWeightUnit(unit);
    setAnchorElWeight(null);
  };

  const alertError = (msg) => {
    setErrorMessage(msg);
    setopenErrorModal(true);
  };

  const checkAllData = () => {
    if (!barcode || barcode.length > 14) {
      alertError("The Barcode should be between 12 and 14 characters long. Please enter a valid Barcode.");
      return false;
    }
    if (!sku || sku.length < 1 || sku.length > 50) {
      alertError("The SKU should be between 1 and 50 characters long. Please enter a valid SKU.");
      return false;
    }
    if (!price || parseFloat(price) <= 0) {
      alertError("The Selling Price should be a possitive number. Please enter a valid Selling Price.");
      return false;
    }
    if (!qty || parseInt(qty) <= 0) {
      alertError("The Quantity should be a possitive number. Please enter a valid Quantity.");
      return false;
    }
    if (!weight || parseFloat(weight) <= 0) {
      alertError("The Weight should be a possitive number. Please enter a valid Weight.");
      return false;
    }
    if (!props.currProduct.title && !props.currProduct.productName) {
      alertError("Title is required. Please enter a product title.");
      return false;
    }
    if (skuUpdate) {
      if (!fulfillmentCenterID) {
        alertError("The Fulfillment Center ID is required. Please enter a valid Fulfillment Center ID.");
        return false;
      }

      const result = validateSchemaForm(schema, productType, formData);
      if (!result.isValid) {
        alertError(
          <>
            <ul style={{ textAlign: 'left' }}>
              {result.errors.map(err => (
                <li>{err}</li>
              ))}
            </ul>
          </>
        );
        return false;
      }
    }
    return true;
  };

  const padCodeToGTIN = (code) => {
    if (code.length < 14) {
      return code.padStart(14, '0');
    }
    return code;
  };

  const handleChangeSFQty = async (quantity) => {
    const body = {
      sku: sku.trim(),
      qty: quantity
    }
    try {
      await fetchWrapper(`${SERVER}/put_update_inventory`, user, password, encodedAuth, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
  };

  const addItem = async () => {
    if (!checkAllData()) {
      return;
    }
    let quantity = qty;

    // const bodyInventory = {
    //   sku: sku.trim(),
    //   shipNode: undefined
    // }
    // try {
    //   const inventoryItem = await fetchWrapper(`${SERVER}/get_inventory`, user, password, encodedAuth, { method: 'POST', body: bodyInventory });
    //   if (inventoryItem.quantity.amount >= 0) {
    //     quantity = parseInt(quantity) + parseInt(inventoryItem.quantity.amount);
    //     handleChangeSFQty(quantity);
    //   }
    // } catch (error) {
    //   console.log("Error: not fetch from get_inventory");
    // }

    setInProgress(true);
    localStorage.setItem('IFSource', source);
    localStorage.setItem('IFFCID', fulfillmentCenterID);
    const updatedSources = Array.from(new Set([source, ...sources])); // Remove duplicates
    setSources(updatedSources);
    localStorage.setItem('IFSources', JSON.stringify(updatedSources));

    let code = padCodeToGTIN(barcode);

    const gtin = props.currProduct.gtin || code || barcode;
    const upc = (props.currProduct.upc || barcode).padStart(12, '0');

    let url = "";
    let body = {};
    let MPItem = [];
    if (skuUpdate) {
      MPItem = [
        {
          Orderable: {
            sku: sku,
            ShippingWeight: parseFloat(weight),
            price: parseFloat(price),
            productIdentifiers: {
              productId: gtin,
              productIdType: "GTIN"
            },
            quantity: parseInt(quantity),
            fulfillmentCenterID: fulfillmentCenterID,
            batteryTechnologyType: batteryTechnologyType,
            chemicalAerosolPesticide: chemicalAerosolPesticide,
            electronicsIndicator: electronicsIndicator,
            stateRestrictions: stateRestrictions.length ? stateRestrictions : [{ 'stateRestrictionsText': "None" }],
            SkuUpdate: "Yes"
          },
          Visible: formData,
        }
      ]
      url = `${SERVER}/post_bulk_mp_item`;
      body = {
        MPItem: MPItem
      }
    } else {
      url = `${SERVER}/send_item_feed_match`;
      body = {
        item: {
          sku: sku.slice(0, 50).trim(),
          upc: upc,
          price: float2Decimal(parseFloat(price)),
          weight: float2Decimal(convertsMass(parseFloat(weight), weightUnit, 'lbs'))
        }
      }
    }

    const metadata = {
      batchName: props.batchName === "none" ? "" : props.batchName,
      email: user,
      productName: (props.currProduct.title || props.currProduct.productName).slice(0, 200),
      productType: productType,
      image: props.currProduct.image,
      sku: sku.slice(0, 50).trim(),
      gtin: gtin,
      upc: upc,
      cost: cost ? parseFloat(cost) : 0,
      qty: quantity ? parseInt(quantity) : 0,
      innerPack: 1,
      source: source,
      note: note,
      expirationDate: expirationDate !== "" ? new Date(expirationDate) : null,
      purchaseDate: purchaseDate !== "" ? new Date(purchaseDate) : null,
      inboundOrderId: "",
      shipmentId: "",
      schema: skuUpdate ? schema : {},
      MPItem: MPItem,
      length: length,
      width: width,
      height: height,
      weight: weight,
      additionalCosts: additionalCosts ? parseFloat(additionalCosts) : 0
    }

    const bodyMetadata = {
      metadata: metadata
    }

    try {
      fetchWrapperNoAuth(`${SERVER}/falcon_insert_metadata`, user, password, { method: 'POST', body: bodyMetadata });
    } catch (error) {
      console.error('Error insert metadata:', error.message);
    }

    try {
      let feedId = "";
      if (!props.multiple) {
        const response = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
        feedId = response.feedId;
      }

      const addedItem = {
        ...props.currProduct,
        availability: "Out_of_stock",
        condition: "New",
        gtin: gtin,
        isDuplicate: false,
        lifecycleStatus: "ACTIVE",
        mart: "WALMART_US",
        price: {
          currency: "USD",
          amount: float2Decimal(parseFloat(price)),
        },
        productName: (props.currProduct.title || props.currProduct.productName).slice(0, 200),
        productType: props.currProduct.productType,
        publishedStatus: "INIT",  // can be PUBLISHED / READY_TO_PUBLISH / IN_PROGRESS / UNPUBLISHED / STAGE / SYSTEM_PROBLEM
        shelf: props.currProduct.shelf,
        sku: sku.slice(0, 50).trim(),
        unpublishedReasons: {
          reason: []
        },
        upc: upc,
        wpid: "",

        status: props.multiple ? "PENDING" : "LOADING",   // can be LOADING / SUCCESS / WARNING / ERROR
        feedStatus: "INIT",                               // can be RECEIVED / INPROGRESS / PROCESSED / ERROR
        feedId: feedId,
        image: props.currProduct.image,
        description: props.currProduct.description,

        qty: parseInt(quantity),
        innerPack: 1,
        itemId: props.currProduct.itemId,
        cost: cost ? parseFloat(cost) : 0,
        length: parseFloat(length),
        width: parseFloat(width),
        height: parseFloat(height),
        weight: float2Decimal(convertsMass(parseFloat(weight), weightUnit, "lbs")),
        brand: brand.slice(0, 60),
        source: source,
        note: note,

        category: categoryName,
        isWfs: false,
        lastRequest: new Date().toISOString(),
        step: 1,
        additionalCosts: additionalCosts ? parseFloat(additionalCosts) : 0,
        MPItem: MPItem
      }

      props.handleAddItem(addedItem);

    } catch (error) {
      console.error('Error:', error.message);
      setInProgress(false);
      return;
    }
    setInProgress(false);
  }

  const calcUsingMarter = () => {
    if (!cost || parseFloat(cost) === 0) {
      alertError('Please type your cost!');
      return;
    }
    setItemId(props.currProduct.itemId)
  }

  const handleAddRestriction = () => {
    const newRestriction = {
      stateRestrictionsText,
      states: stateRestrictionsStates,
      zipCodes: stateRestrictionsZipCodes,
    };

    // Add the new restriction to the array
    setStateRestrictions((prev) => [...prev, newRestriction]);

    // Reset the form inputs
    setStateRestrictionsText("None");
    setStateRestrictionsStates("");
    setStateRestrictionsZipCodes("");
  };

  const handleRemoveRestriction = (index) => {
    setStateRestrictions((prev) => prev.filter((_, i) => i !== index));
  };

  const loadSchema = async (productType) => {
    if (!productType) {
      alertError("Please select Product Type");
      return;
    }
    setLoading(true);
    const url = `${SERVER}/post_get_spec`;
    const body = {
      feedType: "MP_ITEM",
      productTypes: [productType]
    };

    try {
      const data = await fetchWrapper(url, user, password, encodedAuth, { method: 'POST', body: body });
      setSchema(skuUpdate ? data.schema.properties?.MPItem?.items?.properties?.Visible : data.schema.properties?.SupplierItem?.items?.properties?.Visible);
    } catch (err) {
      console.error('Error loading schema:', err);
      setProductType("");
      setProductTypeInput(productType);
      // alertError(`Failed to load schema: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateFormField = (setter) => (value) => setter(value);

  const handleApplySkuBuiler = () => {
    const filteredInputs = skuInputs.filter(input => input.selected);
    if (filteredInputs.length === 0) {
      alertError("SKU Builder configuration is empty.");
      return;
    }
    const skuParts = [];
    for (let input of filteredInputs) {
      switch (input.name) {
        case "Cost Price":
          if (!cost) {
            alertError("Please fill Cost Price field to generate SKU");
            return;
          }
          skuParts.push(parseFloat(cost).toFixed(2));
          break;
        case "Source":
          if (!source) {
            alertError("Please fill Source field to generate SKU");
            return;
          }
          skuParts.push(source.slice(0, 3));
          break;
        case "Expiration Date":
          if (!expirationDate) {
            skuParts.push("ED");
          } else {
            skuParts.push(expirationDate);
          }
          break;
        case "Purchase Date":
          if (!purchaseDate) {
            skuParts.push("PD");
          } else {
            skuParts.push(purchaseDate);
          }
          break;
        case "Note":
          if (!note) {
            skuParts.push(" ");
          } else {
            skuParts.push(note.slice(0, 5));
          }
          break;
        case "Selling Price":
          if (!price) {
            alertError("Please fill Selling Price field to generate SKU");
            return;
          }
          skuParts.push(parseFloat(price).toFixed(2));
          break;
        case "Profit":
          if (!price || !cost) {
            alertError("For profit calculation, you need all these fields: 'Selling Price' and 'Cost Price' to generate a SKU.");
            return;
          }
          const refferalFeesProfit = calcRefFees({ price: { amount: price }, shelf: props.currProduct.shelf });
          const profitProfit = price - cost - refferalFeesProfit;
          skuParts.push(parseFloat(profitProfit).toFixed(2));
          break;
        case "Breakeven":
          if (!price) {
            alertError("For breakeven calculation, you need 'Selling Price' field to generate a SKU.");
            return;
          }
          const refferalFeesBreakeven = calcRefFees({ price: { amount: price }, shelf: props.currProduct.shelf });
          const breakeven = price - refferalFeesBreakeven;
          skuParts.push(parseFloat(breakeven).toFixed(2));
          break;
        case "Quantity":
          if (!qty) {
            alertError("Please fill Quantity field to generate SKU");
            return;
          }
          skuParts.push(qty);
          break;
        case "Barcode":
          if (!barcode) {
            alertError("Please fill Barcode field to generate SKU");
            return;
          }
          skuParts.push(barcode);
          break;
        case "Item ID":
          if (!props.currProduct.itemId) {
            skuParts.push("ID");
          } else {
            skuParts.push(props.currProduct.itemId);
          }
          break;
        case "Brand":
          if (!brand) {
            alertError("Please fill Brand field to generate SKU");
            return;
          }
          skuParts.push(brand);
          break;
        case "Random No.":
          skuParts.push(Math.floor(10000 + Math.random() * 90000));
          break;
        default:
          break;
      }
    }
    const skuBuilder = skuParts.join((skuSeperator && skuSeperator !== "NONE") ? skuSeperator : "");
    if (skuBuilder.length > 50) {
      alertError(<>Your SKU is {skuBuilder.length} characters long, but the limit is 50.<br />Please configure your SKU builder.</>);
      return;
    }

    setSku(skuBuilder);
  }

  // const handleDecimalChange = (e, setValue) => {
  //   let value = e.target.value.replace(/\D/g, "");
  //   if (value) {
  //     value = (parseInt(value, 10) / 100).toFixed(2);
  //   }
  //   setValue(value);
  // };

  useEffect(() => {
    if (props.currProduct) {
      const catName =
        CATEGORIES_SF.find(item => item.name === props.currProduct.category)?.name ??
        CATEGORIES_SF.find(item => item.name.includes(props.currProduct.category))?.name;
      setCategoryName(catName || props.currProduct.category || "");
      setProductType(props.currProduct?.productType);
      setPrice(props.currProduct.price?.amount);
      setWeight(props.currProduct.weight ? (props.currProduct.weight.toFixed(2) || 0) : 0);
      setLength(props.currProduct.length ? (props.currProduct.length.toFixed(2) || 0) : 0);
      setWidth(props.currProduct.width ? (props.currProduct.width.toFixed(2) || 0) : 0);
      setHeight(props.currProduct.height ? (props.currProduct.height.toFixed(2) || 0) : 0);
      setSku(props.currProduct.sku || "");
      setSkuFound((props.currProduct.sku || "") !== "");
      setBrand(props.currProduct.brand || "");
      setBarcode(props.currProduct.Barcode || props.barcode || props.currProduct.upc || "");
      setQty(props.currProduct.qty || "");
      setCost(props.currProduct.cost || getCostPerItemId(props.currProduct.itemId) || "");
      setExpirationDate(props.currProduct?.expiration_date ? new Date(props.currProduct?.expiration_date).toLocaleDateString() : "");
      setPurchaseDate(props.currProduct?.purchase_date ? new Date(props.currProduct?.purchase_date).toLocaleDateString() : new Date().toLocaleDateString());
      setNote(props.currProduct.note || "");
      setAdditionalCosts(props.currProduct.additionalCosts || "");
    }
  }, [props.currProduct]);

  useEffect(() => {
    if (skuUpdate) {
      setSchema(null);
      setFormData({});
      loadSchema(props.currProduct.productType || props.currProduct?.anItem?.productType);
    }
  }, [skuUpdate]);

  const styles = {
    container: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: isSubUser ? 'calc(70vh - 40px)' : "70vh",
      overflowY: 'auto',
      backgroundColor: theme.background.primary,
      border: `2px solid ${theme.border.medium}`,
      borderRadius: '10px',
      p: 2,
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '15px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    content: {
      width: "100%",
      margin: "auto",
      mt: 3,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    button: {
      width: '100%',
      mb: 1,
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
      backgroundColor: theme.button.background,
    },
    helperText: {
      fontFamily: theme.fontFamily,
      color: theme.text.tertiary,
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.6rem",
        lg: "0.57rem",
        xl: "0.65rem",
      },
    },
    gridContainer: {
      color: theme.text.primary,
      mb: 2,
      minWidth: '100%'
    },
    productCard: {
      p: { xs: 1, sm: 2 },
      mb: { xs: 2, sm: 4 },
      border: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
    },
    productInfo: {
      flex: 1,
      color: theme.text.secondary,
    },
    productInfoText: {
      mb: 0.5,
      fontFamily: theme.fontFamily,
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.9rem",
        lg: "0.9rem",
        xl: "0.9rem",
      },
    },
    productTitle: {
      fontFamily: theme.fontFamily,
      mb: 1,
      color: theme.text.primary,
      fontSize: {
        xs: "0.9rem",
        sm: "1.075rem",
        md: "1.1rem",
        lg: "1.1rem",
        xl: "1.1rem",
      },
      wordWrap: "break-word", // Ensures words break to new lines if needed
      overflowWrap: "break-word", // Alternative for better compatibility
      whiteSpace: "normal", // Ensures wrapping happens naturally
    },
    profitCard: {
      border: `1px solid ${theme.border.medium}`,
      bgcolor: theme.background.secondary,
      height: 'auto',
      mb: 2
    },
    profitItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1,
      borderRadius: 1,
    },
    cardHeader: {
      pb: 0,
      '& .MuiCardHeader-title': {
        fontFamily: theme.fontFamily,
        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.2rem', xl: '1.2rem' },
        fontWeight: 'bold',
        color: theme.text.primary
      }
    },
    profitAnalysisText: {
      fontFamily: theme.fontFamily,
      fontWeight: 'medium',
      color: theme.text.primary,
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.9rem",
        lg: "0.9rem",
        xl: "0.9rem",
      },
    },
    profitAnalysisData: {
      fontFamily: theme.fontFamily,
      fontWeight: 'bold',
      fontSize: {
        xs: "0.8rem",
        sm: "0.875rem",
        md: "0.9rem",
        lg: "0.9rem",
        xl: "0.9rem",
      },
    },
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.content}>
        {itemId && <DraggablePopup itemId={itemId} setWmitemId={setItemId} price={cost} />}

        <Grid container sx={styles.gridContainer} spacing={2}>
          <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 6 }}>
            {/* Product Preview Section */}
            {props.currProduct && (
              <Card elevation={3} sx={styles.productCard}>
                <Box sx={{ display: "flex", gap: 2, }}>
                  <img
                    src={props.currProduct.image}
                    alt={props.currProduct.title}
                    style={{
                      width: '100px',
                      height: '100px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      border: `2px solid ${theme.border.light}`,
                      cursor: 'pointer',
                    }}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
                    }}
                    onClick={() => window.open(`https://www.walmart.com/ip/${props.currProduct.itemId}`)}
                  />
                  <Box sx={styles.productInfo}>
                    <Typography variant="h6" sx={styles.productTitle}>
                      {props.currProduct.title || props.currProduct.productName}
                    </Typography>
                    {props.currProduct.brand ? (
                      <Typography sx={styles.productInfoText}>
                        Brand: {brand}
                      </Typography>
                    ) : (
                      <CustomInput
                        size="small"
                        label={"Brand"}
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        type="text"
                        inputProps={{
                          minLength: 1,
                          maxLength: 60,
                        }}
                        helperText={
                          <Typography sx={styles.helperText}>1-60 characters<InfoOutlinedIcon sx={{ fontSize: '16px', }} /></Typography>
                        }
                      />
                    )}

                    <Typography sx={styles.productInfoText}>
                      Buy Box Price: ${parseFloat(props.currProduct.price?.amount).toFixed(2)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', }}>
                      <Typography sx={{ ...styles.productInfoText, mr: { xs: 0, sm: '10px' }, }}>
                        Item ID: {props.currProduct.itemId}
                      </Typography>
                      <Typography
                        sx={{
                          textDecoration: 'underline', mb: 0.5,
                          fontFamily: theme.fontFamily,
                          fontSize: { xs: "0.7rem", sm: "0.775rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem", },
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(`https://www.walmart.com/ip/${props.currProduct.itemId}`)}
                      >
                        open in walmart.com
                      </Typography>
                    </Box>

                    {changeBarcode ? (
                      <Typography sx={styles.productInfoText}>
                        Barcode: {barcode} <IconButton onClick={() => setChangeBarcode(!changeBarcode)}><ChangeCircleOutlinedIcon sx={{ color: theme.text.primary, }} /></IconButton>
                      </Typography>
                    ) : (
                      <CustomInput
                        size="small"
                        label={"Barcode"}
                        value={barcode}
                        onChange={(e) => setBarcode(e.target.value)}
                        type="text"
                        inputProps={{
                          minLength: 12,
                          maxLength: 14,
                        }}
                        isError={!barcode || barcode.length < 12 || barcode.length > 14}
                        helperText={
                          <Typography sx={styles.helperText}>12-14 characters<InfoOutlinedIcon sx={{ fontSize: '16px', }} /></Typography>
                        }
                      />
                    )}
                  </Box>
                </Box>
              </Card>
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
            <Card elevation={3} sx={styles.profitCard}>
              <CardHeader title="Profit Analysis" sx={styles.cardHeader} />
              <CardContent>
                {(() => {
                  const refferalFees = calcRefFees({ price: { amount: price }, shelf: props.currProduct.shelf });
                  const profit = price - cost - refferalFees - (parseFloat(additionalCosts) || 0);
                  const roi = (profit / cost * 100);

                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ ...styles.profitItem, bgcolor: profit > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                        <Typography variant="body1" sx={styles.profitAnalysisText}>
                          Profit:
                        </Typography>
                        <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: profit > 0 ? theme.accent.success : theme.accent.error, }}>
                          {profit < 0 && "-"}${profit < 0 ? (profit * -1).toFixed(2) : profit.toFixed(2)}/unit
                        </Typography>
                      </Box>

                      <Box sx={{ ...styles.profitItem, bgcolor: roi > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                        <Typography variant="body1" sx={styles.profitAnalysisText}>
                          ROI:
                        </Typography>
                        <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: roi > 0 ? theme.accent.success : theme.accent.error, }}>
                          {roi < 0 && "-"}{roi < 0 ? (roi * -1).toFixed(2) : roi.toFixed(2)}%
                        </Typography>
                      </Box>

                      <Box sx={{ ...styles.profitItem, bgcolor: (profit * qty) > 0 ? `${theme.accent.success}15` : `${theme.accent.error}15`, }}>
                        <Typography variant="body1" sx={styles.profitAnalysisText}>
                          Total Profit:
                        </Typography>
                        <Typography variant="body1" sx={{ ...styles.profitAnalysisData, color: (profit * qty) > 0 ? theme.accent.success : theme.accent.error, }}>
                          {(profit * qty) < 0 && "-"}${(profit * qty) < 0 ? ((profit * qty) * -1).toFixed(2) : (profit * qty).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })()}
              </CardContent>
            </Card>

            <WarningInput
              fullWidth
              size="small"
              label={"Note"}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              type="text"
              multiline={true}
              rows={2}
              isWarning={!note}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SourceInput
                fullWidth
                size="small"
                label="Source"
                sources={sources}
                value={source}
                onChange={setSource}
                isWarning={!source}
              />

              <Box>
                <CostPriceInput
                  fullWidth
                  size="small"
                  label={"Cost Price"}
                  value={cost}
                  onChange={(e) => { setCost(e.target.value); setCostPerItemId(props.currProduct.itemId, e.target.value) }}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: (!cost || parseFloat(cost) === 0) ? theme.accent.warning : theme.text.primary, fontFamily: theme.fontFamily }}>
                          $
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={calcUsingMarter}
                          edge="end"
                          sx={{ color: theme.accent.warning }}
                        >
                          <CalculateIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  isWarning={!cost || parseFloat(cost) === 0}
                />
                <Typography sx={{
                  textAlign: 'end',
                  fontFamily: theme.fontFamily,
                  color: theme.accent.warning,
                  mr: '15px',
                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.85rem", lg: "0.9rem", xl: "0.95rem" }
                }}>
                  open Marter widget <TurnSharpRightIcon sx={{ fontSize: { xs: "0.8rem", sm: "1rem", md: "1.1rem", lg: "1.2rem", xl: "1.3rem" } }} />
                </Typography>
              </Box>

              <WarningEvaluateInput
                fullWidth
                size="small"
                label={"Additional Costs"}
                value={additionalCosts}
                onChange={(e) => setAdditionalCosts(e.target.value)}
                onExpressionEvaluated={(result) => {
                  setAdditionalCosts(result);
                }}
                type="text"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: !additionalCosts ? theme.accent.warning : theme.text.primary, fontFamily: theme.fontFamily }}>
                        $
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <HtmlTooltip
                        customtheme={theme}
                        title={
                          <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                            You can enter math expressions like: 5*0.5+10.2 = 12.700
                          </Typography>
                        }
                        arrow
                        placement="top"
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -10],
                                },
                              },
                            ],
                          },
                        }}
                      >
                        <InfoOutlinedIcon sx={{ color: !additionalCosts ? theme.accent.warning : theme.color.primary, cursor: 'default' }} />
                      </HtmlTooltip>
                    </InputAdornment>
                  )
                }}
                isWarning={!additionalCosts}
              />

              <WarningInput
                fullWidth
                size="small"
                label="Expiration Date"
                value={expirationDate}
                onChange={(e) => {
                  setExpirationDate(e.target.value);
                  if (e.target.value) {
                    setExpirationDateInputType('date');
                  }
                }}
                onFocus={() => setExpirationDateInputType('date')}
                onBlur={() => {
                  if (!expirationDate) {
                    setExpirationDateInputType('text');
                  }
                }}
                type={expirationDateInputType}
                placeholder="mm/dd/yyyy"
                isWarning={!expirationDate}
                InputProps={{
                  inputProps: {
                    max: "2030-12-31"
                  }
                }}
              />

              <WarningInput
                fullWidth
                size="small"
                label="Purchase Date"
                value={purchaseDate}
                onChange={(e) => {
                  setPurchaseDate(e.target.value);
                  if (e.target.value) {
                    setPurchaseDateInputType('date');
                  }
                }}
                onFocus={() => setPurchaseDateInputType('date')}
                onBlur={() => {
                  if (!purchaseDate) {
                    setPurchaseDateInputType('text');
                  }
                }}
                type={purchaseDateInputType}
                placeholder="mm/dd/yyyy"
                isWarning={!purchaseDate}
                InputProps={{
                  inputProps: {
                    max: "2030-12-31"
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid container sx={styles.gridContainer} spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 3, }} >
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1 }}>
                Basic Information
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={skuUpdate}
                    onChange={(e) => setSkuUpdate(e.target.checked)}
                    sx={{
                      color: theme.color.secondary,
                      '&.Mui-checked': {
                        color: theme.color.primary,
                      },
                    }}
                  />
                }
                label={<Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>SKU Update</Typography>}
              />

              <Box>
                <CustomInput
                  fullWidth
                  size="small"
                  label={"SKU"}
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  type="text"
                  inputProps={{
                    minLength: 1,
                    maxLength: 50,
                  }}
                  isError={!sku || sku.length < 1 || sku.length > 50}
                  helperText={
                    <Typography sx={styles.helperText}>
                      {skuFound ? "This item has a SKU and can't be changed." : "Enter 1-50 characters."}<InfoOutlinedIcon sx={{ fontSize: '16px', }} />
                    </Typography>
                  }
                />

                <Typography variant="body2" sx={{ fontFamily: theme.fontFamily, mt: 1 }}>SKU Builder</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
                  <Button variant="contained" sx={styles.button} onClick={() => handleApplySkuBuiler()}>Apply</Button>
                  <Button variant="contained" sx={styles.button} onClick={() => setOpenSkuBuilderModal(true)}>Configure</Button>
                </Box>
              </Box>

              {!productType && (
                <ProductTypeSelector
                  loadSchema={loadSchema}
                  setProductType={setProductType}
                  productType={productType}
                  inputValue={productTypeInput}
                  setInputValue={setProductTypeInput}
                  productTypes={PRODUCT_TYPES.map(product => product.name)}
                />
              )}
            </Box>
          </Grid>

          {/* Basic Information */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <CustomInput
                fullWidth
                size="small"
                label={"Selling Price"}
                value={price}
                onChange={(e) => setPrice(parseFloat(e.target.value))}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: !price || parseFloat(price) <= 0 ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily }}>
                        $
                      </Typography>
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  min: 0,
                  max: 1000000000000000,
                  step: 0.01
                }}
                isError={!price || parseFloat(price) <= 0}
              />

              <CustomInput
                fullWidth
                size="small"
                label={"Quantity"}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: (!qty || parseInt(qty) < 1) ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily }}>
                        #
                      </Typography>
                    </InputAdornment>
                  )
                }}
                inputProps={{
                  min: 1,
                  max: 10000000,
                  step: 1
                }}
                isError={!qty || parseInt(qty) < 1}
              />

              {skuUpdate && <CustomInput
                fullWidth
                size="small"
                label={"Fulfillment Center ID"}
                value={fulfillmentCenterID}
                onChange={(e) => setFulfillmentCenterID(e.target.value)}
                type="text"
                inputProps={{
                  minLength: 1,
                  maxLength: 100,
                }}
                isError={!fulfillmentCenterID || fulfillmentCenterID.length < 1 || fulfillmentCenterID.length > 100}
                helperText={
                  <Typography sx={{ ...styles.helperText, cursor: 'pointer' }} onClick={() => window.open('https://seller.walmart.com/settings/shipping-profile/seller-fulfillment')}>
                    Click to see FC's<InfoOutlinedIcon sx={{ fontSize: '16px', }} />
                  </Typography>
                }
              />}

              {/* Weight */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CustomInput
                  fullWidth
                  size="small"
                  label={"Shipping Weight"}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <HtmlTooltip
                          customtheme={theme}
                          title={
                            <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                              units found on {props.currProduct.weightUnit || "lbs"}
                            </Typography>
                          }
                          arrow
                          placement="top"
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -10],
                                  },
                                },
                              ],
                            },
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', }}
                            id="IFWeightTypography"
                            aria-controls={openWeight ? 'IFWeightSelect' : undefined}
                            aria-haspopup="true"
                            aria-expanded={openWeight ? 'true' : undefined}
                            onClick={(event) => setAnchorElWeight(event.currentTarget)}
                          >
                            <Typography sx={{ color: weight ? theme.text.primary : theme.accent.error, fontFamily: theme.fontFamily, }}>{weightUnit}</Typography>
                            <ArrowDropDownOutlinedIcon sx={{ color: weight ? theme.text.primary : theme.accent.error, }} />
                          </Box>
                          <Menu
                            id="IFWeightSelect"
                            anchorEl={anchorElWeight}
                            open={openWeight}
                            onClose={() => setAnchorElWeight(null)}
                            MenuListProps={{
                              'aria-labelledby': 'IFWeightTypography',
                            }}
                          >
                            <MenuItem key={"lbs"} selected={"lbs" === weightUnit} onClick={() => handleWeightUnitChange("lbs")}>
                              lbs
                            </MenuItem>
                            <MenuItem key={"ounce"} selected={"ounce" === weightUnit} onClick={() => handleWeightUnitChange("ounce")}>
                              ounce
                            </MenuItem>
                            <MenuItem key={"mg"} selected={"mg" === weightUnit} onClick={() => handleWeightUnitChange("mg")}>
                              mg
                            </MenuItem>
                            <MenuItem key={"gram"} selected={"gram" === weightUnit} onClick={() => handleWeightUnitChange("gram")}>
                              gram
                            </MenuItem>
                            <MenuItem key={"kg"} selected={"kg" === weightUnit} onClick={() => handleWeightUnitChange("kg")}>
                              kg
                            </MenuItem>
                            <MenuItem key={"lbs.oz"} selected={"lbs.oz" === weightUnit} onClick={() => handleWeightUnitChange("lbs.oz")}>
                              lbs.oz
                            </MenuItem>
                          </Menu>
                        </HtmlTooltip>

                      </InputAdornment>
                    )
                  }}
                  inputProps={{
                    min: 0,
                    max: 100000000000,
                    step: 0.01
                  }}
                  isError={!weight || parseFloat(weight) <= 0}
                />
                {props.currProduct.hasOwnProperty("weightFromAz") && props.currProduct.weightFromAz && (
                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily, fontSize: 12 }}>
                        weight found on amazon.com
                      </Typography>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <InfoOutlinedIcon size={10} sx={{ color: theme.color.primary, cursor: 'pointer', }} />
                  </HtmlTooltip>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SelectComponent
                value={categoryName}
                title={"Category"}
                onChange={setCategoryName}
                options={CATEGORIES_SF}
                size={"small"}
                required={true}
              />

              {skuUpdate && <SelectComponent
                value={electronicsIndicator}
                title={"Contains Electronic Component?"}
                onChange={setElectronicsIndicator}
                options={[
                  { value: "Yes", name: "Yes" },
                  { value: "No", name: "No" }
                ]}
                size={"small"}
              />}

              {skuUpdate && <SelectComponent
                value={chemicalAerosolPesticide}
                title={"Contains Chemical, Aerosol or Pesticide?"}
                onChange={setChemicalAerosolPesticide}
                options={[
                  { value: "Yes", name: "Yes" },
                  { value: "No", name: "No" }
                ]}
                size={"small"}
              />}

              {skuUpdate && <SelectComponent
                value={batteryTechnologyType}
                title={"Contained Battery Type"}
                onChange={setBatteryTechnologyType}
                options={[
                  { value: "Does Not Contain a Battery", name: "Does Not Contain a Battery" },
                  { value: "Mercury", name: "Mercury" },
                  { value: "Carbon Zinc", name: "Carbon Zinc" },
                  { value: "Lead Acid (Non-Spillable)", name: "Lead Acid (Non-Spillable)" },
                  { value: "Alkaline", name: "Alkaline" },
                  { value: "Lithium Primary (Lithium Metal)", name: "Lithium Primary (Lithium Metal)" },
                  { value: "Nickel Cadmium", name: "Nickel Cadmium" },
                  { value: "Lithium Ion", name: "Lithium Ion" },
                  { value: "Nickel Metal Hydride", name: "Nickel Metal Hydride" },
                  { value: "Lead Acid", name: "Lead Acid" },
                  { value: "Silver", name: "Silver" },
                  { value: "Magnesium", name: "Magnesium" }
                ]}
                size={"small"}
                required={true}
              />}

            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <SelectComponent
                value={stateRestrictionsText}
                title={"Restriction Type"}
                onChange={updateFormField(setStateRestrictionsText)}
                options={[
                  { value: "None", name: "None" },
                  { value: "Illegal for Sale", name: "Illegal for Sale" },
                  { value: "Commercial", name: "Commercial" },
                ]}
                size={"small"}
              />
              {stateRestrictionsText !== "None" && (
                <CustomInput
                  fullWidth
                  size="small"
                  label={"State Codes"}
                  value={stateRestrictionsStates}
                  onChange={(e) => updateFormField(setStateRestrictionsStates(e.target.value))}
                  type="text"
                  isError={!stateRestrictionsStates || stateRestrictionsStates === ""}
                />
              )}
              {stateRestrictionsText !== "None" && (
                <CustomInput
                  fullWidth
                  size="small"
                  label={"Zip Codes"}
                  value={stateRestrictionsZipCodes}
                  onChange={(e) => updateFormField(setStateRestrictionsZipCodes(e.target.value))}
                  type="number"
                  isError={!stateRestrictionsZipCodes || parseInt(stateRestrictionsZipCodes) === 0}
                />
              )}
              {stateRestrictionsText !== "None" && (
                <Button onClick={handleAddRestriction}>
                  <AddIcon />
                </Button>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <ul>
                {stateRestrictions.map((restriction, index) => (
                  <li key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box>
                        <strong>Type:</strong> {restriction.stateRestrictionsText}<br />
                        <strong>States:</strong> {restriction.states}<br />
                        <strong>ZIP Codes:</strong> {restriction.zipCodes}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
                        <IconButton
                          onClick={() => handleRemoveRestriction(index)}
                          sx={{ color: theme.accent.error }} // Set color for the IconButton
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>

                  </li>
                ))}
              </ul>
            </Box>
          </Grid>

          {skuUpdate && <Grid size={12}>
            {schema ? (
              <Box sx={{ mb: 4 }}>
                <SchemaForm
                  schema={schema}
                  currProduct={props.currProduct}
                  productType={productType}
                  formData={formData}
                  setFormData={setFormData}
                  isConvert={!skuUpdate}
                />
              </Box>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <EnhancedSpinningLogo />
              </Box>
            ) : (
              <Alert severity="error">
                <Typography sx={{ fontFamily: theme.fontFamily }}>Please select Product Type</Typography>
              </Alert>
            )}
          </Grid>}
        </Grid>

        <Box
          sx={{
            position: 'sticky',
            bottom: 16,
            right: 16,
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 100,
            width: 'auto',
            ml: 'auto',
          }}
        >
          <Button
            variant="contained"
            sx={{
              mb: 1,
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              border: `1px solid ${theme.border.light}`,
              borderRadius: '10px',
              color: theme.text.negative,
              backgroundColor: theme.color.primary,
            }}
            onClick={() => addItem()}
          >
            {inProgress ? (
              <CustomCircularProgress color={theme.text.negative} />
            ) : (
              <>
                Add Item
              </>
            )}
          </Button>
        </Box>
      </Box>

      <SKUBuilderModal
        open={openSkuBuilderModal}
        setOpen={setOpenSkuBuilderModal}
        confirm={() => { localStorage.setItem('IFSKUInputs', JSON.stringify(skuInputs)); localStorage.setItem('IFSKUSeperator', skuSeperator) }}
        inputs={skuInputs}
        setInputs={setSkuInputs}
        seperator={skuSeperator}
        setSeperator={setSkuSeperator}
        reset={resetSkuInputs}
      />

      <ErrorModal open={openErrorModal} setOpen={setopenErrorModal} message={errorMessage} />
    </Box>
  );
}

export default FormSF;
